import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { useOnImportFinished } from '../../../../../components/csv-import/data/hooks/use-on-import-finished';
import { ImportStatus } from '../../../../../components/csv-import/data/types/import-status';
import { ImportType } from '../../../../../components/csv-import/data/types/import-type';
import style from '../../../../../components/grid-header/shared-classes.module.less';
import { useEnableBundledProducts } from '../../../../../feature-flags/hooks/use-enable-bundled-products';
import { useEnableDangerousGoods } from '../../../../../feature-flags/hooks/use-enable-dangerous-goods';
import { useIsUnitsOfMeasureAvailable } from '../../../../../feature-gates/use-is-units-of-measure-available';
import { useEnableNewProductImport } from '../../../../../feature-flags/hooks/use-enable-new-product-import';
import { useModal } from '../../../../../hooks/use-modal';
import Global from '../../../../../store/global-selectors';
import { Workflows } from '../../../../../tracking/tracking-constants';
import { WorkflowSteps, useTrackWorkflow } from '../../../../../tracking/use-workflow-tracking';
import { ALIAS_MODAL_TYPE, PRODUCT_MODAL_TYPE } from '../../constants/import-products';
import { ImportBundlesModal } from '../../modals/import/bundle-import-modal';
import ImportModal from '../../modals/import/import-modal';
import { useProductContext } from '../../product-context';
import { DangerousGoodsImportDrawer } from '../../modals/import/dangerous-goods-import-drawer/dangerous-goods-import-drawer';
import { ProductImportDrawer } from '../../modals/import/product-import-drawer/product-import-drawer';
import { ProductUomImportDrawer } from '../../modals/import/product-uom-import-drawer/product-uom-import-drawer';
import { messages } from './messages';

const useIsBundlesImportEnabled = () => {
  return useEnableBundledProducts();
};

export const ImportButton = () => {
  const { open: openAliasModal, close: closeAliasModal, isOpen: isAliasModalOpen } = useModal();
  const { open: openProductDrawer, close: closeProductDrawer, isOpen: isProductDrawerOpen } = useModal();
  const { open: openBundlesModal, isOpen: isBundlesModalOpen, close: closeBundlesModal } = useModal();
  const { open: openDangerousGoodsDrawer, close: closeDangerousGoodsDrawer, isOpen: isDangerousGoodsDrawerOpen } = useModal();
  const { open: openProductUomDrawer, close: closeProductUomDrawer, isOpen: isProductUomDrawerOpen } = useModal();

  const trackOldImportProcess = useTrackWorkflow(Workflows.PRODUCT_IMPORT_FLOW);

  const openOldAliasModal = () => {
    trackOldImportProcess(WorkflowSteps.START, {
      importType: ALIAS_MODAL_TYPE,
    });
    openAliasModal();
  };

  const openOldProductModal = () => {
    trackOldImportProcess(WorkflowSteps.START, {
      importType: PRODUCT_MODAL_TYPE,
    });
    openProductDrawer();
  };

  const trackNewImportProcess = useTrackWorkflow(Workflows.CSV_PRODUCT_IMPORT_FLOW);
  const trackDangerousGoodsProcess = useTrackWorkflow(Workflows.CSV_DANGEROUS_GOODS_IMPORT_FLOW);
  const trackProductUomProcess = useTrackWorkflow(Workflows.CSV_PRODUCT_UOM_IMPORT_FLOW);

  const latestProductImport = useSelector((state) => Global.selectCsvImportType(state, ImportType.product));
  const latestDangerousGoodsImport = useSelector((state) => Global.selectCsvImportType(state, ImportType.dangerousGoods));
  const latestProductUomImport = useSelector((state) => Global.selectCsvImportType(state, ImportType.productUom));
  // Before you create a new import, the old one must be completed.
  // So if you open the modal again when the old import has not finished, it will redirect you to the last step of the process.
  // So we don't want to send a new message (track new process) that the user has started a new import process, because they haven't actually started.
  // It simply checks the status of the current import.
  const openNewProductModal = () => {
    if (latestProductImport.status !== ImportStatus.ACTIVE) {
      trackNewImportProcess(WorkflowSteps.START, { version: 2 });
    }
    openProductDrawer();
  };

  const openDangerousGoodsModal = () => {
    if (latestDangerousGoodsImport.status !== ImportStatus.ACTIVE) {
      trackDangerousGoodsProcess(WorkflowSteps.START, { version: 2 });
    }
    openDangerousGoodsDrawer();
  };

  const openProductUomModal = () => {
    if (latestProductUomImport.status !== ImportStatus.ACTIVE) {
      trackProductUomProcess(WorkflowSteps.START, { version: 2 });
    }
    openProductUomDrawer();
  };

  const isBundlesImportEnabled = useIsBundlesImportEnabled();
  const isNewProductImportEnabled = useEnableNewProductImport();
  const isDangerousGoodsEnabled = useEnableDangerousGoods();
  const { isAvailable: isUnitsOfMeasureEnabled } = useIsUnitsOfMeasureAvailable();

  const { refreshGridData } = useProductContext();

  // we want to refresh the grid only when the user is in the context of the modal...
  // otherwise we may disturb user's current work.
  useOnImportFinished(
    ImportType.product,
    useCallback(() => {
      if (isProductDrawerOpen && isNewProductImportEnabled) {
        refreshGridData();
      }
    }, [isNewProductImportEnabled, isProductDrawerOpen, refreshGridData]),
  );

  useOnImportFinished(
    ImportType.dangerousGoods,
    useCallback(() => {
      if (isDangerousGoodsDrawerOpen) {
        refreshGridData();
      }
    }, [isDangerousGoodsDrawerOpen, refreshGridData]),
  );

  useOnImportFinished(
    ImportType.productUom,
    useCallback(() => {
      if (isProductUomDrawerOpen) {
        refreshGridData();
      }
    }, [isProductUomDrawerOpen, refreshGridData]),
  );

  return (
    <>
      <DropdownMenu closeWhenChildrenClicked dropdownMenuClassName={style['dropdown-menu']} menuPosition='bottom-right'>
        <DropdownMenuToggler className={style['action-button']} theme='tertiary'>
          <FormattedMessage {...messages.productGridImportsButton} />
        </DropdownMenuToggler>
        <DropdownMenuItem
          className={style['dropdown-menu-item']}
          onClick={isNewProductImportEnabled ? openNewProductModal : openOldProductModal}
        >
          <FormattedMessage {...messages.importProductsOption} />
        </DropdownMenuItem>
        <DropdownMenuItem className={style['dropdown-menu-item']} onClick={openOldAliasModal}>
          <FormattedMessage {...messages.importAliasesOption} />
        </DropdownMenuItem>
        {isDangerousGoodsEnabled && (
          <DropdownMenuItem className={style['dropdown-menu-item']} onClick={openDangerousGoodsModal}>
            <FormattedMessage {...messages.importDangerousGoodsOption} />
          </DropdownMenuItem>
        )}
        {isUnitsOfMeasureEnabled && (
          <DropdownMenuItem className={style['dropdown-menu-item']} onClick={openProductUomModal}>
            <FormattedMessage {...messages.importProductUomOption} />
          </DropdownMenuItem>
        )}
        {isBundlesImportEnabled && (
          <DropdownMenuItem className={style['dropdown-menu-item']} onClick={openBundlesModal}>
            <FormattedMessage {...messages.importBundlesOption} />
          </DropdownMenuItem>
        )}
      </DropdownMenu>

      {isProductDrawerOpen &&
        (isNewProductImportEnabled ? (
          <ProductImportDrawer onClose={closeProductDrawer} trackProcess={trackNewImportProcess} />
        ) : (
          <ImportModal modalType={PRODUCT_MODAL_TYPE} onClose={closeProductDrawer} />
        ))}
      {isAliasModalOpen && <ImportModal modalType={ALIAS_MODAL_TYPE} onClose={closeAliasModal} />}
      {isBundlesModalOpen && <ImportBundlesModal onClose={closeBundlesModal} />}
      {isDangerousGoodsDrawerOpen && (
        <DangerousGoodsImportDrawer onClose={closeDangerousGoodsDrawer} trackProcess={trackDangerousGoodsProcess} />
      )}
      {isProductUomDrawerOpen && <ProductUomImportDrawer onClose={closeProductUomDrawer} trackProcess={trackProductUomProcess} />}
    </>
  );
};
